import React, { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList'; // TODO: change to custom icon
import {
    Box,
    Theme,
    Drawer,
    useTheme,
    useAppSelector,
    Form,
    useAppDispatch,
} from '../../base';
import {
    emptyJobsBoardForm,
    IJobsBoardForm,
    TOP_HEADER_HEIGHT,
} from '../../common';
import {
    Container,
    FiltersContainer,
    MobileFiltersButton,
} from './JobsBoard.styles';
import { jobsActions, jobsSelectors } from '../../actions';
import BoardFilters from './BoardFilters';
import JobsGrid from './JobsGrid';
import { useForm } from 'react-hook-form';
import EmptyState from './EmptyState';
import { Grid } from '../../base';
import { useEffectOnce } from 'react-use';
import PargodaLoading from '../PargodaLoading';
import debounce from 'lodash.debounce';

const JobsBoard: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md')
    );
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const dispatch = useAppDispatch();

    const methods = useForm<IJobsBoardForm>({
        mode: 'onSubmit',
        defaultValues: emptyJobsBoardForm,
    });

    const { watch, formState } = methods;
    const { dirtyFields } = formState;
    const hasFiltersApplied = Object.keys(dirtyFields).length > 0;

    const queryParams = new URLSearchParams(location.search);
    const companyKey = queryParams.get('ck') || '';
    const isInitialCandidatesJobsLoading = useAppSelector(
        jobsSelectors.getIsInitialCandidatesJobsLoading
    );
    const candidatesJobs = useAppSelector(jobsSelectors.getCandidatesJobs);

    const formValues = watch();

    useEffectOnce(() => {
        dispatch(jobsActions.fetchPublicTrackableSources());
    });

    const debouncedOnChange = useMemo(
        () =>
            debounce((formValues, hasFiltersApplied) => {
                dispatch(
                    jobsActions.fetchCandidatesJobs({
                        companyKey,
                        filters: hasFiltersApplied ? formValues : undefined,
                    })
                );
            }, 300),
        [300]
    );

    useEffect(() => {
        debouncedOnChange(formValues, hasFiltersApplied);
    }, [companyKey, dispatch, hasFiltersApplied, JSON.stringify(formValues)]);

    const renderFilters = () => {
        if (isMobile) {
            return (
                <>
                    <MobileFiltersButton
                        color="primary"
                        onClick={() => setIsFilterDrawerOpen(true)}
                    >
                        <FilterListIcon />
                    </MobileFiltersButton>
                    <Drawer
                        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                        open={isFilterDrawerOpen}
                        onClose={() => setIsFilterDrawerOpen(false)}
                    >
                        <Box sx={{ width: 280, p: 2 }}>
                            <Form methods={methods}>
                                <BoardFilters />
                            </Form>
                        </Box>
                    </Drawer>
                </>
            );
        }

        return (
            <FiltersContainer>
                <Form methods={methods}>
                    <BoardFilters />
                </Form>
            </FiltersContainer>
        );
    };

    const renderContent = () => {
        if (candidatesJobs?.length === 0) {
            if (hasFiltersApplied) {
                return (
                    <>
                        {renderFilters()}
                        <Grid
                            container
                            spacing={3}
                            sx={{
                                width: '100%',
                                height: `calc(100vh - ${TOP_HEADER_HEIGHT}px)`,
                                overflow: 'auto',
                                justifyContent: 'center',
                                alignItems: 'center',
                                paddingBottom: 3,
                            }}
                        >
                            <EmptyState isInline={true} />
                        </Grid>
                    </>
                );
            } else {
                return <EmptyState />;
            }
        }

        return (
            <>
                {renderFilters()}
                <JobsGrid />
            </>
        );
    };

    return (
        <Container>
            {!isInitialCandidatesJobsLoading ? (
                renderContent()
            ) : (
                <PargodaLoading height={600} width={600} />
            )}
        </Container>
    );
};

export default JobsBoard;
